/**
 * Отделы нового бэка — `/v2/departments`.
 *
 * Legacy-группа `/departments` из спеки v2 исчезла вместе с остальным
 * битриксовым слоем, поэтому у экранов v2 свой источник. Состав отдела бэк
 * отдаёт отдельным маршрутом и сразу списком id — вытаскивать его из
 * пользователей, как на legacy, не нужно.
 *
 * Удаления отдела у бэка нет: только создание, чтение и правка состава.
 */
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type DepartmentV2 =
  SchemaV2<'internal_departments_handler.departmentResponse'>;
export type DepartmentCreateV2 =
  SchemaV2<'internal_departments_handler.departmentCreateRequest'>;

export async function fetchDepartmentsV2(): Promise<DepartmentV2[]> {
  const { data } = await http.get<{ result?: DepartmentV2[] }>(
    '/v2/departments'
  );

  return Array.isArray(data?.result) ? data.result : [];
}

/** Состав отдела — массив id пользователей. */
export async function fetchDepartmentMembersV2(
  departmentId: string
): Promise<string[]> {
  const { data } = await http.get<{ result?: string[] }>(
    `/v2/departments/${departmentId}/members`
  );

  return Array.isArray(data?.result) ? data.result : [];
}

export async function createDepartmentV2(
  payload: DepartmentCreateV2
): Promise<DepartmentV2> {
  const { data } = await http.post<DepartmentV2>('/v2/departments', payload);

  return data;
}

export async function addDepartmentMemberV2(
  departmentId: string,
  userId: string
): Promise<void> {
  await http.post(
    `/v2/departments/${encodeURIComponent(departmentId)}/members`,
    { userId }
  );
}

export async function removeDepartmentMemberV2(
  departmentId: string,
  userId: string
): Promise<void> {
  await http.delete(
    `/v2/departments/${encodeURIComponent(departmentId)}/members/${encodeURIComponent(userId)}`
  );
}

export const departmentsV2Keys = {
  all: ['departments-v2'] as const,
  members: (id: string) => ['departments-v2', id, 'members'] as const,
};

export function useDepartmentsV2() {
  return useQuery({
    queryKey: departmentsV2Keys.all,
    queryFn: fetchDepartmentsV2,
    staleTime: 5 * 60 * 1000,
  });
}

/** Отдел со списком id сотрудников — форма, которую ждёт `RRolePicker`. */
export interface DepartmentWithMembersV2 {
  id: string;
  name: string;
  memberIds: string[];
}

/**
 * Отделы вместе с составом. Состав тянется по одному запросу на отдел —
 * общего маршрута «все отделы с людьми» у бэка нет; запросы кэшируются на 5
 * минут, так что открытие формы второй раз сети не создаёт.
 */
export function useDepartmentsWithMembersV2(
  enabled = true
): DepartmentWithMembersV2[] {
  const listQ = useDepartmentsV2();
  const departments = listQ.data ?? [];

  const memberQueries = useQueries({
    queries: departments.map((dept) => ({
      queryKey: departmentsV2Keys.members(dept.id ?? ''),
      queryFn: () => fetchDepartmentMembersV2(dept.id as string),
      enabled: enabled && Boolean(dept.id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  return useMemo(
    () =>
      departments
        .map((dept, index) => ({
          id: dept.id ?? '',
          name: String(dept.name ?? '').trim(),
          memberIds: (memberQueries[index]?.data as string[]) ?? [],
        }))
        // Пустые отделы в «Добавить всех» не нужны — добавлять нечего.
        .filter((dept) => dept.id && dept.name && dept.memberIds.length > 0),
    // memberQueries — новый массив на каждый рендер, поэтому сравниваем по
    // данным, а не по ссылке.
    [listQ.data, memberQueries.map((q) => q.data).join('|')]
  );
}

/** Состав одного отдела — для экрана управления. */
export function useDepartmentMembersV2(departmentId: string | undefined) {
  return useQuery({
    queryKey: departmentsV2Keys.members(departmentId ?? ''),
    queryFn: () => fetchDepartmentMembersV2(departmentId as string),
    enabled: Boolean(departmentId),
  });
}

/** Создание отдела и правка состава. Без оптимистики: ждём ответ бэка. */
export function useDepartmentActionsV2(departmentId?: string) {
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: (payload: DepartmentCreateV2) => createDepartmentV2(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: departmentsV2Keys.all }),
  });

  const refreshMembers = () =>
    qc.invalidateQueries({
      queryKey: departmentsV2Keys.members(departmentId ?? ''),
    });

  const addMember = useMutation({
    mutationFn: (userId: string) =>
      addDepartmentMemberV2(departmentId as string, userId),
    onSuccess: refreshMembers,
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      removeDepartmentMemberV2(departmentId as string, userId),
    onSuccess: refreshMembers,
  });

  return {
    create,
    addMember,
    removeMember,
    isBusy: create.isPending || addMember.isPending || removeMember.isPending,
  };
}
